import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';
import type { Membership, Scope, Invitation } from './memberships';

const API_BASE = import.meta.env.VITE_IDENTITY_API ?? '/api/v1';

export type DirectoryEntry = {
  membership: Membership;
  user?: { id: string; email: string; display_name: string };
  display_name?: string;
  email?: string;
  teams?: string[];
};

export type DirectorySnapshot = {
  tenant_id: string;
  scope_type?: string;
  organization_id?: string;
  workspace_id?: string;
  total: number;
  members: DirectoryEntry[];
  generated_at: string;
};

export type SearchResult = {
  query: string;
  count: number;
  results: DirectoryEntry[];
  generated_at: string;
};

export type TeamDashboard = {
  team: { id: string; name: string; slug: string; description?: string };
  member_count: number;
  sub_team_count: number;
  members: Array<{ id: string; user_id: string; role: string; status: string }>;
  recent_activity?: Array<{ action: string; summary?: string; user_id?: string; created_at: string }>;
  generated_at: string;
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function collabFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!res.ok) throw new Error(`Collaboration ${path} failed: ${res.status} ${await res.text()}`);
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const collaborationApi = {
  directory: (params?: {
    scope_type?: string;
    organization_id?: string;
    workspace_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.scope_type) qs.set('scope_type', params.scope_type);
    if (params?.organization_id) qs.set('organization_id', params.organization_id);
    if (params?.workspace_id) qs.set('workspace_id', params.workspace_id);
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return collabFetch<DirectorySnapshot>(`/member-directory${query ? `?${query}` : ''}`);
  },
  search: (q: string, params?: { scope_type?: string; organization_id?: string; workspace_id?: string }) => {
    const qs = new URLSearchParams({ q });
    if (params?.scope_type) qs.set('scope_type', params.scope_type);
    if (params?.organization_id) qs.set('organization_id', params.organization_id);
    if (params?.workspace_id) qs.set('workspace_id', params.workspace_id);
    return collabFetch<SearchResult>(`/member-search?${qs.toString()}`);
  },
  bulkInvite: (invitations: Array<{ email?: string; identity_id?: string; scope: Scope; message?: string }>) =>
    collabFetch<{ created: Invitation[]; failed?: Array<{ index: number; message: string }> }>('/invitations/bulk', {
      method: 'POST',
      body: JSON.stringify({ invitations }),
    }),
  resendInvitation: (id: string) => collabFetch<Invitation>(`/invitations/${id}/resend`, { method: 'POST' }),
  cancelInvitation: (id: string) => collabFetch<Invitation>(`/invitations/${id}`, { method: 'DELETE' }),
  teamDashboard: (id: string) => collabFetch<TeamDashboard>(`/teams/${id}/dashboard`),
};
